import { useMemo } from 'react';
import FixedDropdownPicker from './FixedDropdownPicker';
import {
  buildExpectedGraduationYearOptions,
  EXPECTED_GRADUATION_MONTH_OPTIONS,
  type ExpectedGraduationMonth,
} from '../../data/expectedGraduationOptions';
import {
  facultyForcesProgramNa,
  isQueensStandardFaculty,
  QUEENS_FACULTY_OTHER,
  QUEENS_FACULTY_SPECIAL_OPTIONS,
  QUEENS_PROGRAM_NA,
  QUEENS_PROGRAM_OTHER,
  QUEENS_PROGRAM_SPECIAL_OPTIONS,
  queensFacultyPickerLabel,
  type QueensFacultyPickerId,
  type QueensProgramKind,
} from '../../data/queensAcademicPickerConstants';
import {
  QUEENS_FACULTY_OPTIONS,
  programsForQueensFaculty,
  queensFacultyLabel,
} from '../../data/queensUniversityAcademics';

export interface QueensAcademicSelection {
  facultyId: QueensFacultyPickerId | null;
  facultyCustom: string;
  programKind: QueensProgramKind | null;
  programListValue: string | null;
  programOtherText: string;
  expectedGraduationMonth: ExpectedGraduationMonth | null;
  expectedGraduationYear: string | null;
}

interface QueensFacultyProgramPickerProps {
  value: QueensAcademicSelection;
  onChange: (next: QueensAcademicSelection) => void;
  className?: string;
}

const CUSTOM_FIELD_CLASS =
  'mt-2 w-full rounded-md border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-950 px-3 py-2.5 text-base text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-400';

function programPickerValue(selection: QueensAcademicSelection): string | null {
  if (facultyForcesProgramNa(selection.facultyId)) return QUEENS_PROGRAM_NA;
  if (selection.programKind === 'na') return QUEENS_PROGRAM_NA;
  if (selection.programKind === 'other') return QUEENS_PROGRAM_OTHER;
  return selection.programListValue;
}

function programSelectedLabel(selection: QueensAcademicSelection): string | null {
  if (facultyForcesProgramNa(selection.facultyId)) return 'N/A';
  if (selection.programKind === 'na') return 'N/A';
  if (selection.programKind === 'other') {
    const trimmed = selection.programOtherText.trim();
    return trimmed ? trimmed : 'Other';
  }
  return selection.programListValue;
}

export default function QueensFacultyProgramPicker({
  value,
  onChange,
  className = 'space-y-5',
}: QueensFacultyProgramPickerProps) {
  const facultyOptions = useMemo(
    () => [
      ...QUEENS_FACULTY_OPTIONS.map((faculty) => ({ value: faculty.id, label: faculty.label })),
      ...QUEENS_FACULTY_SPECIAL_OPTIONS.map((option) => ({ value: option.value, label: option.label })),
    ],
    [],
  );

  const facultySelectedLabel = useMemo(
    () =>
      queensFacultyPickerLabel(
        value.facultyId,
        value.facultyCustom,
        isQueensStandardFaculty(value.facultyId) ? queensFacultyLabel(value.facultyId) : null,
      ),
    [value.facultyId, value.facultyCustom],
  );

  const programListOptions = useMemo(() => {
    if (!isQueensStandardFaculty(value.facultyId)) return [];
    return programsForQueensFaculty(value.facultyId).map((program) => ({
      value: program,
      label: program,
    }));
  }, [value.facultyId]);

  const programOptions = useMemo(
    () => [...programListOptions, ...QUEENS_PROGRAM_SPECIAL_OPTIONS],
    [programListOptions],
  );

  const programFooterOptions = useMemo(() => {
    if (programListOptions.length === 0) return [];
    return QUEENS_PROGRAM_SPECIAL_OPTIONS;
  }, [programListOptions.length]);

  const graduationYearOptions = useMemo(() => buildExpectedGraduationYearOptions(), []);

  const programLocked = facultyForcesProgramNa(value.facultyId);

  const handleFacultyChange = (facultyId: string) => {
    const nextId = facultyId as QueensFacultyPickerId;
    if (facultyForcesProgramNa(nextId)) {
      onChange({
        ...value,
        facultyId: nextId,
        facultyCustom: '',
        programKind: 'na',
        programListValue: null,
        programOtherText: '',
      });
      return;
    }
    if (nextId === QUEENS_FACULTY_OTHER) {
      onChange({
        ...value,
        facultyId: nextId,
        programKind: null,
        programListValue: null,
        programOtherText: '',
      });
      return;
    }
    onChange({
      ...value,
      facultyId: nextId,
      facultyCustom: '',
      programKind: null,
      programListValue: null,
      programOtherText: '',
    });
  };

  const handleProgramChange = (picked: string) => {
    if (picked === QUEENS_PROGRAM_NA) {
      onChange({
        ...value,
        programKind: 'na',
        programListValue: null,
        programOtherText: '',
      });
      return;
    }
    if (picked === QUEENS_PROGRAM_OTHER) {
      onChange({
        ...value,
        programKind: 'other',
        programListValue: null,
      });
      return;
    }
    onChange({
      ...value,
      programKind: 'list',
      programListValue: picked,
      programOtherText: '',
    });
  };

  return (
    <div className={className}>
      <div>
        <label
          id="prospect-queens-faculty-label"
          htmlFor="prospect-queens-faculty"
          className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Faculty at Queen&apos;s
        </label>
        <FixedDropdownPicker
          id="prospect-queens-faculty"
          ariaLabelledBy="prospect-queens-faculty-label"
          value={value.facultyId}
          options={facultyOptions}
          selectedLabel={facultySelectedLabel}
          placeholder="Select faculty"
          onChange={handleFacultyChange}
        />
        {value.facultyId === QUEENS_FACULTY_OTHER && (
          <input
            type="text"
            id="prospect-queens-faculty-custom"
            value={value.facultyCustom}
            onChange={(event) => onChange({ ...value, facultyCustom: event.target.value })}
            placeholder="Enter your faculty"
            className={CUSTOM_FIELD_CLASS}
            autoComplete="off"
          />
        )}
      </div>

      <div>
        <label
          id="prospect-queens-program-label"
          htmlFor="prospect-queens-program"
          className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Program
        </label>
        <FixedDropdownPicker
          id="prospect-queens-program"
          ariaLabelledBy="prospect-queens-program-label"
          value={programPickerValue(value)}
          options={programOptions}
          footerOptions={programFooterOptions}
          selectedLabel={programSelectedLabel(value)}
          placeholder={
            programLocked
              ? 'N/A'
              : isQueensStandardFaculty(value.facultyId)
                ? 'Select program'
                : value.facultyId === QUEENS_FACULTY_OTHER
                  ? 'Select or describe program'
                  : 'Select a faculty first'
          }
          disabled={programLocked || !value.facultyId}
          searchable={isQueensStandardFaculty(value.facultyId)}
          searchPlaceholder="Search programs…"
          onChange={handleProgramChange}
        />
        {value.programKind === 'other' && !programLocked && (
          <input
            type="text"
            id="prospect-queens-program-custom"
            value={value.programOtherText}
            onChange={(event) => onChange({ ...value, programOtherText: event.target.value })}
            placeholder="Enter your program"
            className={CUSTOM_FIELD_CLASS}
            autoComplete="off"
          />
        )}
      </div>

      <div>
        <p
          id="prospect-expected-graduation-label"
          className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
        >
          Expected graduation
        </p>
        <div className="grid grid-cols-2 gap-3">
          <FixedDropdownPicker
            id="prospect-expected-graduation-month"
            ariaLabelledBy="prospect-expected-graduation-label"
            value={value.expectedGraduationMonth}
            options={EXPECTED_GRADUATION_MONTH_OPTIONS}
            placeholder="Month"
            onChange={(expectedGraduationMonth) => {
              onChange({
                ...value,
                expectedGraduationMonth: expectedGraduationMonth as ExpectedGraduationMonth,
              });
            }}
          />
          <FixedDropdownPicker
            id="prospect-expected-graduation-year"
            ariaLabelledBy="prospect-expected-graduation-label"
            value={value.expectedGraduationYear}
            options={graduationYearOptions}
            placeholder="Year"
            onChange={(expectedGraduationYear) => {
              onChange({
                ...value,
                expectedGraduationYear,
              });
            }}
          />
        </div>
      </div>
    </div>
  );
}
